import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import type { LoyaltyPointEventTypeToken } from '@sleed/bizcommerce-api';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { formatDate } from '~/lib/format-date';
import { useLoyaltyPerEntityList } from '~/hooks/use-loyalty-per-entity';
import { APPOINTMENTS_MODULE, useHasSpecialModule } from '~/lib/auth/use-has-special-module';
import { NEW_APPOINTMENT_EVENT, tokenToForm, type LoyaltyEventTypeFormValues } from '~/lib/schemas/loyalty-event-type';
import { PerEntityEditDialog } from './per-entity-edit-dialog';

/** Loyalty points-per-entity (Ανάθεση πόντων) — edit-only grid of point-awarding event types. */
export function LoyaltyPerEntityGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useLoyaltyPerEntityList();
	// The "new appointment" event belongs to the optional Appointments module (BW-8453): hidden while it is off.
	const appointments = useHasSpecialModule(APPOINTMENTS_MODULE);
	const rows = useMemo(() => (data ?? []).filter(o => appointments || o.e_tp !== NEW_APPOINTMENT_EVENT), [data, appointments]);

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<LoyaltyEventTypeFormValues | null>(null);

	const columns = useMemo<SystemGridColumn<LoyaltyPointEventTypeToken>[]>(
		() => [
			{
				key: 'name',
				header: t('loyalty.perEntity.columns.name'),
				width: '220px',
				sortValue: o => o.nm ?? '',
				exportValue: o => o.nm ?? '',
				render: o => <span className="truncate text-sm font-medium text-foreground">{o.nm || '—'}</span>
			},
			{
				key: 'description',
				header: t('loyalty.perEntity.columns.description'),
				width: 'minmax(0,1fr)',
				sortValue: o => o.descr ?? '',
				exportValue: o => o.descr ?? '',
				render: o => (
					<span className="truncate text-sm text-foreground-muted" title={o.descr ?? ''}>
						{o.descr || '—'}
					</span>
				)
			},
			{
				key: 'points',
				header: t('loyalty.perEntity.columns.points'),
				width: '100px',
				align: 'right',
				sortValue: o => o.pnt ?? 0,
				exportValue: o => o.pnt ?? 0,
				render: o => <span className="text-sm font-medium tabular-nums text-foreground">{o.pnt ?? 0}</span>
			},
			{
				key: 'activeFrom',
				header: t('loyalty.perEntity.columns.activeFrom'),
				width: '150px',
				sortValue: o => (o.afrom ? String(o.afrom) : ''),
				exportValue: o => (o.afrom ? String(o.afrom) : ''),
				render: o => <span className="text-sm text-foreground-muted">{formatDate(o.afrom)}</span>
			},
			{
				key: 'activeTo',
				header: t('loyalty.perEntity.columns.activeTo'),
				width: '150px',
				defaultVisible: false,
				sortValue: o => (o.ato ? String(o.ato) : ''),
				exportValue: o => (o.ato ? String(o.ato) : ''),
				render: o => <span className="text-sm text-foreground-muted">{formatDate(o.ato)}</span>
			},
			{
				key: 'updated',
				header: t('loyalty.perEntity.columns.updated'),
				width: '150px',
				defaultVisible: false,
				sortValue: o => (o.crt ? String(o.crt) : ''),
				exportValue: o => (o.crt ? String(o.crt) : ''),
				render: o => <span className="text-sm text-foreground-muted">{formatDate(o.crt)}</span>
			}
		],
		[t]
	);

	return (
		<>
			<SystemGrid<LoyaltyPointEventTypeToken>
				gridId="loyalty-per-entity"
				title={t('loyalty.perEntity.title')}
				rows={rows}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={o => o.id ?? o.s_cd ?? ''}
				columns={columns}
				searchPredicate={(o, q) => `${o.nm ?? ''} ${o.descr ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'name', dir: 'asc' }}
				emptyIcon={<Sparkles size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				onEdit={o => {
					setEditing(tokenToForm(o));
					setEditOpen(true);
				}}
				exportFilename="loyalty-per-entity"
			/>

			<PerEntityEditDialog open={editOpen} onOpenChange={setEditOpen} initial={editing} />
		</>
	);
}
